import * as React from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { updateTrainingCertificateAsync } from './CertificationStore/CertificationStoreSlice';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

const BUSINESS_VERTICALS = [
  { value: 'atheneum', label: 'Atheneum' },
  { value: 'iimtt', label: 'IIMTT' },
];

// Backend stores awardDate as an ISO datetime; the date input needs
// YYYY-MM-DD only, so trim it down when pre-filling the form.
const toDateInputValue = (isoDate) => {
  if (!isoDate) return '';
  return new Date(isoDate).toISOString().slice(0, 10);
};

const EditTrainingCertificate = ({ certificate, onSuccess, onClose }) => {
  const [form, setForm] = React.useState({
    studentRegistrationNum: certificate.studentRegistrationNum || '',
    studentName: certificate.studentName || '',
    trainingCentre: certificate.trainingCentre || '',
    awardDate: toDateInputValue(certificate.awardDate),
    awardClassification: certificate.awardClassification || '',
    courseType: certificate.courseType || '',
    businessVertical: certificate.businessVertical || '',
  });

  const dispatch = useDispatch();
  const { updateLoading } = useSelector((state) => {
    const { trainingCertificateReducer } = state;

    return {
      updateLoading:
        trainingCertificateReducer && trainingCertificateReducer.updateLoading,
    };
  });

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Only sends fields that actually changed, so an empty/unmodified update
  // never fires — matches the backend's rejection of an empty update body.
  const getChangedFields = () => {
    const changed = {};
    Object.keys(form).forEach((key) => {
      const original =
        key === 'awardDate'
          ? toDateInputValue(certificate.awardDate)
          : certificate[key] || '';
      if (form[key] !== original) {
        changed[key] = form[key];
      }
    });
    return changed;
  };

  const updateCertificate = async () => {
    const changedFields = getChangedFields();

    if (Object.keys(changedFields).length === 0) {
      toast.error('No changes to update.');
      return;
    }

    try {
      dispatch(
        updateTrainingCertificateAsync({
          id: certificate._id,
          payload: changedFields,
        })
      )
        .unwrap()
        .then(() => {
          toast.success('Certificate was updated successfully.');
          if (onSuccess) onSuccess();
        })
        .catch((error) => {
          toast.error(
            (error && error.message) || 'Certificate could not be updated.'
          );
        });
    } catch (error) {
      toast.error('Certificate could not be updated.');
    }
  };

  return (
    <Box sx={style}>
      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box>
        <Typography>Edit Training Certificate</Typography>
        <Box alignItems="center">
          <Box mt={2} />
          <Box>
            <TextField
              fullWidth
              label="Student Registration Num"
              variant="outlined"
              size="small"
              value={form.studentRegistrationNum}
              onChange={handleChange('studentRegistrationNum')}
            />
          </Box>

          <Box mt={2} />
          <Box>
            <TextField
              fullWidth
              label="Student Name"
              variant="outlined"
              size="small"
              value={form.studentName}
              onChange={handleChange('studentName')}
            />
          </Box>

          <Box mt={2} />
          <Box>
            <TextField
              fullWidth
              label="Training Centre"
              variant="outlined"
              size="small"
              value={form.trainingCentre}
              onChange={handleChange('trainingCentre')}
            />
          </Box>

          <Box mt={2} />
          <Box>
            <TextField
              fullWidth
              label="Award Date"
              type="date"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.awardDate}
              onChange={handleChange('awardDate')}
            />
          </Box>

          <Box mt={2} />
          <Box>
            <TextField
              fullWidth
              label="Award Classification"
              variant="outlined"
              size="small"
              value={form.awardClassification}
              onChange={handleChange('awardClassification')}
            />
          </Box>

          <Box mt={2} />
          <Box>
            <TextField
              fullWidth
              label="Course Type"
              variant="outlined"
              size="small"
              value={form.courseType}
              onChange={handleChange('courseType')}
            />
          </Box>

          <Box mt={2} />
          <Box>
            <TextField
              select
              fullWidth
              label="Business Vertical"
              variant="outlined"
              size="small"
              value={form.businessVertical}
              onChange={handleChange('businessVertical')}>
              {BUSINESS_VERTICALS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box my={2} display="flex" justifyContent="flex-end">
          <Box mr={1}>
            <Button variant="outlined" color="primary" onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={updateCertificate}
            disabled={updateLoading}>
            {updateLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default EditTrainingCertificate;
